/**
 * Created by fajar on 01/16/2019.
 *
 * Common auth user and router middleware
 * current user session/role process should be logic here
 */

import { cloneDeep } from "lodash";
import { Deserialize } from "cerialize";

import Storage from "./Storage";

import { getRouter } from "./util/GlobalRouter";

import User from "./entity/User";
import AsesmenPendaftar from "./entity/AsesmenPendaftar";
import { useAuthStore } from "./stores/auth.store";
import { pinia } from "./stores";

export default class Authentication {

    private static authKey: string = "user";

    private static defaultPath: string = "/";

    private static homePath: string = "/index";

    private static asesmenKey: string = "asesmen";

    private static asesmenPath: string = "/e-asesmen";

    /**
     * Method isLogin to get login state
     * user must be object to valid it is login
     *
     * @returns {boolean} user has login or not
     */
    /**
     * Method isLogin to get login state
     *
     * @returns {boolean} user has login or not
     */
    public static isLogin() {
        const authStore = useAuthStore(pinia);
        authStore.hydrate(); // Ensure state is loaded from cookies
        return authStore.isLoggedIn;
    }

    /**
     * Method getUser to get user from store
     *
     * @param key {string} key of user (ex: username)
     * @return user {Object | User} instance
     */
    public static getUser(key: string = "") {
        const authStore = useAuthStore(pinia);
        authStore.hydrate(); // Ensure state is loaded from cookies

        // Special case: token should come from store state directly
        // since it's stored separately and more reliable
        if (key === 'token') {
            return authStore.token || '';
        }

        const user = authStore.currentUser;

        // If no user, return empty User or undefined based on key request
        if (!user) {
            return key !== "" ? undefined : new User();
        }

        // If key specified, return that property directly
        if (key !== "") {
            return user[key];
        }

        // Return full user object, deserialize if needed
        if (!(user instanceof User)) {
            return Deserialize(user, User);
        }

        return user;
    }

    /**
     * Method setUser to save user in cookies and redirect path
     *
     * @param param object user
     * @param isUpdate boolean set user method is update or new
     */
    public static setUser(param: User | any, isUpdate: boolean = false) {
        const authStore = useAuthStore(pinia);

        if (isUpdate) {
            // Merge logic if needed, or defineStore handles it?
            // Original logic: delete param['token'], merge with current.
            // Let's keep existing logic but apply to Store state
            delete param["token"];
            const currentUser = authStore.currentUser || new User();
            param = Object.assign(cloneDeep(currentUser), param);
        }

        // This set user logic should probably just call store.setUser
        // But store.setUser doesn't persist to cookie!
        // Wait, I updated authStore.login to persist.
        // But setUser might be called from elsewhere (UpdateProfile?)

        // Let's assume usage of setUser is rare outside login. 
        // But we must maintain persistence here if we want full compat.

        Storage.getCookie().set(Authentication.authKey, Deserialize(param, User));
        authStore.setUser(Deserialize(param, User));

        if (!isUpdate && Authentication.isLogin()) {
            getRouter().push(Authentication.homePath);
        }
    }

    /**
     * Method removeUser to delete user in cookies
     * dependence delete all cookies too
     * and then redirect to root path
     *
     * @param allData {boolean} is remove all local storage too?
     */
    public static removeUser(allData: boolean = true) {
        const authStore = useAuthStore(pinia);
        authStore.logout(); // This handles clearing storage and state
    }

    /**
     * Auth middleware to hook validation before route navigation
     * implement more logic here
     *
     * @param to {Object} route will be leave
     * @param from {Object} route will be next
     * @param next {Function} next action (callback)
     */
    public static middleware(to: Object, from: Object, next: Function) {
        const requiredAuth: any = to["matched"].some((record: Object) => record["meta"].requiredAuth);
        const deniedForAuth: any = to["matched"].some((record: Object) => record["meta"].deniedForAuth);

        if (requiredAuth && !Authentication.isLogin()) {
            return next({ path: Authentication.defaultPath });
        }

        if (Authentication.isLogin()) {
            if (Storage.isCookieExpired()) {
                window.location.href = '/';

                return
            }

            if (deniedForAuth) {
                return next({ path: Authentication.homePath });
            }
        }

        return next();
    }

    public static setAsesmen(param: AsesmenPendaftar) {
        Storage.getCookie().set(Authentication.asesmenKey, Deserialize(param, AsesmenPendaftar));
        getRouter().push(Authentication.asesmenPath);
    }

    public static getAsesmen(key: string = "") {
        let asesmenPendaftar: AsesmenPendaftar | undefined = Storage.getCookie().get(Authentication.asesmenKey);

        // deserialize as AsesmenPendaftar instance if not instance of
        if (!(asesmenPendaftar instanceof AsesmenPendaftar)) {
            asesmenPendaftar = Deserialize((asesmenPendaftar || {}), AsesmenPendaftar);
        }

        return (key !== "") ? asesmenPendaftar[key] : asesmenPendaftar;
    }

    public static removeAsesmen() {
        Storage.getCookie().remove(Authentication.asesmenKey);
        Storage.getLocalStorage().remove();
    }

    /**
     * Get merchant from localStorage or user object
     * 
     * @returns merchant object or null
     */
    public static getMerchant(): any {
        // Try to get from localStorage first
        const merchantFromStorage = Storage.getLocalStorage().get('merchant');
        if (merchantFromStorage) {
            return merchantFromStorage;
        }

        // Fallback to user object
        const user: User = Authentication.getUser();
        if (user && user.merchant) {
            return user.merchant;
        }

        return null;
    }

    /**
     * Get merchant_kode from merchant
     * 
     * @returns merchant_kode string or default 'SALWA'
     */
    public static getMerchantKode(): string {
        const merchant = Authentication.getMerchant();
        if (merchant && merchant.kode) {
            return merchant.kode;
        }

        // Fallback to query parameter
        const router = getRouter();
        if (router && router.currentRoute && router.currentRoute.query && router.currentRoute.query.merchant_kode) {
            return router.currentRoute.query.merchant_kode as string;
        }

        // Last fallback
        return 'SALWA';
    }

}
